import { buildDirectoryChildSpecs } from '../common/branch-directory.js';
import { claimIdentity } from '../../../webmodule-common/other/api/user-security.js';
import {
  containsSameText,
  containsText,
  isEmptyOrSpace,
  validId
} from '../../../webmodule-common/other/ui/string-helper-functions.js';
import { currentUserClaims } from '../../../webmodule-common/other/currentuser-claims.js';
import { customElement, query, state } from 'lit/decorators.js';
import { emptyGuid } from '../../../webmodule-common/other/api/guid.js';
import { EventGuidAction } from '../data/events.js';
import {
  FranchiseeBranchDeploymentView,
  FranchiseeBranchDeploymentViewEvent
} from '../data/franchisee-branch-deployment-view.js';
import { getAssetCdnPath } from '../../../webmodule-common/other/common/assets.js';
import { getLicenseData, LicensePoolData } from '../../supplier/common/supplier-license-data.js';
import { getTerritoryManager } from '../../settings/data/territory-manager.js';
import { html, TemplateResult } from 'lit';
import { information } from '../../../webmodule-common/other/ui/modal-option.js';
import { LitElementLangBase } from '../../../webmodule-common/other/litelement-base';
import { sleep } from '../../../webmodule-common/other/general/time.js';
import {
  TenantInitializationState,
  ViewDeploymentTenants
} from '../../api/supplier-api-interface-franchiseenetwork.js';
import { TimedTrigger } from '../../../webmodule-common/other/timed-trigger.js';
import { tlang } from '../../../webmodule-common/other/language/lang.js';
import { userDataStore } from '../../supplier/common/current-user-data-store.js';
import {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../webmodule-common/components/src/webmodule-components.js';

export interface FranchiseeSummaryTableOptions {
  deploymentId: number;
  onOpen: EventGuidAction;
  onLogin: FranchiseeBranchDeploymentViewEvent;
}

/**
 * A dealership (franchisee) plus its branch rows. The table groups by dealership: the branches
 * are ordinary rows within the group, the dealership name + status render in the group header, and
 * the dealership-level values (V6 code, contact, licenses) render in the group footer. Every branch
 * row also carries its dealership's summary values so the header/footer can read them from any member.
 */
export type FranchiseeNetworkRow = FranchiseeBranchDeploymentView & {
  /** Dealership-level "licenses used" (the parent value), surfaced in the group footer. */
  dealershipLicensesUsed?: number;
  children?: FranchiseeNetworkRow[];
};

export interface FranchiseeNetworkTableData {
  branchDeployments: FranchiseeNetworkRow[];
  licenseData: LicensePoolData | undefined;
}

/**
 * How many dealership (tenant) rows convertData builds per synchronous chunk before yielding to
 * the event loop. Small enough to keep the tab responsive on large networks, large enough that
 * the extra yields add negligible total latency.
 */
const CONVERT_CHUNK_SIZE = 50;

@customElement('webmodule-franchisee-network')
export class WebModuleFranchiseeNetwork extends LitElementLangBase {
  @query('#branches-table')
  table?: WebModuleTable;
  @state()
  private _viewDeploymentTenants: ViewDeploymentTenants | undefined;
  @state()
  private _data?: FranchiseeNetworkTableData;
  private territoryManager = getTerritoryManager();
  @state()
  private _filter?: string;

  timedTrigger?: TimedTrigger;

  /** Stable group key (by dealership) so the table receives the same function reference each render. */
  private readonly groupByDealership = (item: object): string => String((item as FranchiseeNetworkRow).tenantId);

  /** Group header band: dealership name (opens the franchisee editor) followed by a status badge. */
  private readonly renderGroupHeader = (value: unknown): TemplateResult => {
    const dealership = this.data?.branchDeployments.find(d => String(d.tenantId) === String(value));
    if (!dealership) return html`<span class="branch-table-franchisee-name">${String(value)}</span>`;
    const openFranchisee = (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.editFranchisee(dealership);
    };
    return html`
      <a class="branch-table-franchisee-name" href="#" @click=${openFranchisee}>${dealership.sortName}</a>
      <webmodule-tag
        class="dealership-status-badge"
        size="small"
        variant=${getTenantStatusVariant(dealership.status, dealership.inUse)}
      >
        ${getTenantStatusDisplayValue(dealership.status, dealership.inUse)}
      </webmodule-tag>
    `;
  };

  public get viewDeploymentTenants(): ViewDeploymentTenants | undefined {
    return this._viewDeploymentTenants;
  }

  public set viewDeploymentTenants(value: ViewDeploymentTenants | undefined) {
    this._viewDeploymentTenants = value;
    this.convertData(); //fires off a promise
  }

  public get data(): FranchiseeNetworkTableData | undefined {
    return this._data;
  }

  public set data(value: FranchiseeNetworkTableData | undefined) {
    this._data = value;
    this.table?.fetchEvent();
  }

  async convertData() {
    const results: FranchiseeNetworkRow[] = [];
    const userSecurity = currentUserClaims();
    const viewDeployment = this.viewDeploymentTenants;

    await this.territoryManager.needsTerritories();
    let territoryId = userSecurity.claim(claimIdentity.supplierDealerTerritory);
    if (!validId(territoryId)) {
      territoryId = emptyGuid;
    }
    const activeTerritoryBranchIds = !validId(territoryId)
      ? undefined
      : this.territoryManager.getBranchLinksByTerritory(territoryId)?.map(x => x.branchId);

    if (!viewDeployment) {
      this.data = { branchDeployments: [], licenseData: undefined };
      return;
    }
    await userDataStore.loadCoreDetails();
    if (!this.isConnected || this.viewDeploymentTenants !== viewDeployment) return;
    const cacheData = userDataStore.allFranchiseeDetails;

    const tenants = viewDeployment.tenants ?? [];
    const processTenant = (tenantInfo: (typeof tenants)[number]) => {
      const tenant = cacheData?.tenantList.tenants?.find(x => x.id === tenantInfo.tenantId);

      const franchiseeDeploymentConfiguration = cacheData?.franchisees.find(
        x => x.franchiseeTenantId == tenantInfo.tenantId && x.dealerDeploymentId == viewDeployment.dealerDeploymentId
      );
      const branches = (cacheData?.branches ?? []).filter(
        x =>
          x.dealerDeploymentId == franchiseeDeploymentConfiguration?.dealerDeploymentId &&
          x.franchiseeTenantId == franchiseeDeploymentConfiguration?.franchiseeTenantId
      );

      // Territory with an empty access list means no access at all.
      // Branch subset filtering happens after attribution so index 0 stays main.
      if (activeTerritoryBranchIds !== undefined && activeTerritoryBranchIds.length === 0) {
        return;
      }

      const branchReferences = cacheData?.branchReferences.find(
        x => x.dealerDeplymentId === viewDeployment.dealerDeploymentId && x.tenantId === tenant?.id
      );

      if (!tenant) return;
      const userAccess = viewDeployment.userLoginInformation?.find(x => x.tenantId === tenant.id && x.lastLoginTime);
      const primaryContact = viewDeployment?.tenants?.find(x => x.tenantId === tenantInfo.tenantId)?.primaryContact;

      const dealershipRow: FranchiseeNetworkRow = {
        allowSupportAccess: tenantInfo.allowSupplierSupportAccess /* && status is available */,
        branchName: '',
        branchNameAbbreviated: '',
        branchV6CustomerCode: '',
        deploymentDomain: viewDeployment.uiDomain ?? '',
        deploymentId: viewDeployment.dealerDeploymentId,
        franchiseeDeploymentConfigId: franchiseeDeploymentConfiguration?.id ?? emptyGuid,
        LicensesUsed: tenantInfo.licensePoolAllocated ?? 0,
        LicensePoolAllocations: tenantInfo.licensePoolAllocations ?? 0,
        name: tenant.companyName ?? '',
        primaryContact: primaryContact ?? undefined,
        reference: tenantInfo.reference ?? '',
        status: tenantInfo.state ?? TenantInitializationState.Available,
        tenantId: tenantInfo.tenantId,
        branchId: '',
        isBranch: false,
        inUse: userAccess !== undefined,
        fabricatorModeEnabled: franchiseeDeploymentConfiguration?.fabricatorModeEnabled ?? false,
        sortName: tenant.companyName ?? '',
        sortBranchName: '',
        children: []
      };
      const tenantUsers = viewDeployment.userLoginInformation?.filter(x => x.tenantId === tenantInfo.tenantId) ?? [];

      // Cache returns DateCreated order per tenant/deployment (main = index 0).
      // Attribute licenses over the full list first; territory and hidden filters
      // then drop children. Empty result hides the dealership.
      const unavailableLabel = tlang`Unavailable`;
      let childSpecs = buildDirectoryChildSpecs(
        branches,
        tenantUsers,
        claimIdentity.branchId,
        emptyGuid,
        unavailableLabel
      );
      if (activeTerritoryBranchIds !== undefined) {
        // Case-insensitive, like the attribution beside it: territory branch links and the branch
        // cache are separate GUID stores. An exact match risks dropping every child here, and an
        // empty child list hides the whole dealership - a silent failure that reads as lost access.
        childSpecs = childSpecs.filter(spec => containsSameText(spec.branchId, activeTerritoryBranchIds));
      }
      if (childSpecs.length === 0) return;

      results.push(dealershipRow);

      for (const spec of childSpecs) {
        if (spec.kind === 'unavailable') {
          // No branch data synced yet. LicensesUsed uses full-list attribution
          // (invalid/missing claims home to main = this placeholder).
          dealershipRow.children!.push({
            allowSupportAccess: tenantInfo.allowSupplierSupportAccess /* && status is available */,
            branchName: unavailableLabel,
            branchNameAbbreviated: tlang``,
            branchV6CustomerCode: '',
            deploymentDomain: viewDeployment.uiDomain ?? '',
            deploymentId: viewDeployment.dealerDeploymentId,
            franchiseeDeploymentConfigId: franchiseeDeploymentConfiguration?.id ?? emptyGuid,
            LicensesUsed: spec.licensesUsed,
            LicensePoolAllocations: tenantInfo.licensePoolAllocations ?? 0,
            dealershipLicensesUsed: tenantInfo.licensePoolAllocated ?? 0,
            name: '',
            primaryContact: primaryContact ?? undefined,
            reference: tenantInfo.reference ?? '',
            status: tenantInfo.state ?? TenantInitializationState.Available,
            tenantId: tenantInfo.tenantId,
            branchId: emptyGuid,
            isBranch: true,
            inUse: userAccess !== undefined,
            fabricatorModeEnabled: franchiseeDeploymentConfiguration?.fabricatorModeEnabled ?? false,
            sortName: tenant.companyName ?? '',
            sortBranchName: unavailableLabel
          });
          continue;
        }

        // Visible branch row. Guard unknown kinds / missing source index.
        if (spec.kind !== 'visible-branch') continue;
        const branch = branches[spec.sourceIndex];
        if (!branch) continue;

        dealershipRow.children!.push({
          allowSupportAccess: tenantInfo.allowSupplierSupportAccess /* && status is available */,
          branchName: branch.name /* not supporting branches in the UI yet */,
          branchNameAbbreviated: branch.abbreviation,
          branchV6CustomerCode: branchReferences?.referenceOverrides?.[branch.branchId] ?? '',
          deploymentDomain: viewDeployment.uiDomain ?? '',
          deploymentId: viewDeployment.dealerDeploymentId,
          franchiseeDeploymentConfigId: franchiseeDeploymentConfiguration?.id ?? emptyGuid,
          LicensesUsed: spec.licensesUsed,
          LicensePoolAllocations: tenantInfo.licensePoolAllocations ?? 0,
          // Dealership-level licenses used (parent value), carried on every branch so the group
          // footer can show it from any member row.
          dealershipLicensesUsed: tenantInfo.licensePoolAllocated ?? 0,
          name: '',
          primaryContact: primaryContact ?? undefined,
          reference: tenantInfo.reference ?? '',
          status: tenantInfo.state ?? TenantInitializationState.Available,
          tenantId: tenantInfo.tenantId,
          branchId: branch.branchId,
          isBranch: true,
          inUse: userAccess !== undefined,
          fabricatorModeEnabled: franchiseeDeploymentConfiguration?.fabricatorModeEnabled ?? false,
          sortName: tenant.companyName ?? '',
          sortBranchName: branch.name
        });
      }
    };

    // Build the dealership rows in chunks, yielding to the event loop between chunks. Building a
    // large network in one synchronous pass blocks the main thread, freezing the tab (no spinner
    // animation, no navigation) until it finishes; yielding keeps the page responsive.
    for (let i = 0; i < tenants.length; i++) {
      if (i > 0 && i % CONVERT_CHUNK_SIZE === 0) {
        await sleep(0);
        // Bail out if the view was disposed or a newer convertData run superseded this one.
        if (!this.isConnected || this.viewDeploymentTenants !== viewDeployment) return;
      }
      processTenant(tenants[i]);
    }

    const licenseData = await getLicenseData(viewDeployment.dealerDeploymentId);
    if (!this.isConnected || this.viewDeploymentTenants !== viewDeployment) return;
    this.data = {
      // Only the dealership (parent) rows go into the table; their branches render
      // as always-open child rows. Sort dealerships by name.
      branchDeployments: results.sort((a, b) => a.sortName.localeCompare(b.sortName)),
      licenseData
    };
  }

  render() {
    // Table groups by dealership. Child rows are branches. Header shows
    // name + status; footer reads dealership fields via groupAggregate from any member.
    const keyEvent = (item: FranchiseeBranchDeploymentView) => `${item.tenantId}-${item.branchId}`;
    const columns: WebModuleTableColumnDef[] = [];
    columns.push({
      title: tlang`%%branch%% Name`,
      minWidth: '200px',
      fieldName: 'branchName',
      fade: true,
      // Anchor label so the aggregate row reads as the dealership summary.
      groupAggregate: () => html`<span style="opacity: 0.6;">${tlang`Dealership`}</span>`,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as FranchiseeBranchDeploymentView;
        // Do not treat emptyGuid as blank: that is the Unavailable placeholder.
        const clickItem = async (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
          if (rowItem.branchId === emptyGuid) {
            await information(tlang`Not available yet`);
            return;
          }
          this.editBranch(rowItem);
        };
        const label = isEmptyOrSpace(rowItem.branchNameAbbreviated)
          ? rowItem.branchName
          : `${rowItem.branchName} (${rowItem.branchNameAbbreviated})`;
        return html`<a href="#" @click=${clickItem}>${label}</a>`;
      }
    });
    columns.push({
      title: tlang`V6 Customer Code`,
      width: '150px',
      fieldName: 'v6CustomerCode',
      fade: true,
      groupAggregate: groupItems => html`${(groupItems[0] as FranchiseeNetworkRow).reference}`,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as FranchiseeBranchDeploymentView;
        const clickItem = async (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
          if (rowItem.branchId === emptyGuid) {
            await information(tlang`Not available yet`);
            return;
          }
          this.editBranch(rowItem);
        };
        if (isEmptyOrSpace(rowItem.branchV6CustomerCode)) return html``;
        return html`<a href="#" @click=${clickItem}>${rowItem.branchV6CustomerCode}</a>`;
      }
    });
    columns.push({
      title: tlang`Primary %%contact%%`,
      minWidth: '160px',
      fieldName: 'primaryContact',
      // Dealership-level value; shown only in the group footer.
      groupAggregate: groupItems => html`${(groupItems[0] as FranchiseeNetworkRow).primaryContact?.friendlyName ?? ''}`,
      displayValue: () => html``
    });
    columns.push({
      title: tlang`Licenses Allocated`,
      width: '140px',
      fieldName: 'licensesAllocated',
      groupAggregate: groupItems => {
        const dealership = groupItems[0] as FranchiseeNetworkRow;
        //if the value is -1 then it means that the dealer is using this supplier
        //but their license pool and license is not owned by the supplier
        //could be owned by Cyncly etc.
        //this is less likely to happen outside of the demo environment
        if ((dealership.dealershipLicensesUsed ?? 0) < 0) return tlang`N/A`;
        return this.zeroAsDashTemplate(dealership.LicensePoolAllocations ?? 0);
      },
      displayValue: () => html``
    });
    columns.push({
      title: tlang`Licenses Used`,
      width: '120px',
      fieldName: 'licensesUsed',
      groupAggregate: groupItems => {
        const used = (groupItems[0] as FranchiseeNetworkRow).dealershipLicensesUsed ?? 0;
        if (used < 0) return tlang`N/A`;
        return html`<span style="font-weight: 700;">(${used})</span>`;
      },
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as FranchiseeBranchDeploymentView;
        if (rowItem.LicensesUsed < 0) return tlang`N/A`;
        return this.zeroAsDashTemplate(rowItem.LicensesUsed);
      }
    });
    columns.push({
      title: tlang`Login to %%branch%%`,
      width: '120px',
      align: 'center',
      fieldName: 'login',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as FranchiseeBranchDeploymentView;
        // Unavailable (emptyGuid) still shows the key and reports "Not available yet" on click.
        const clickEvent = (_e: Event) => this.loginToFranchisee(rowItem);
        // A webmodule-icon-button styles itself inside the table's shadow DOM (the
        // old global .btn-circle button classes can't reach in there). The icon paints
        // with currentColor, so setting `color` to the themed --webmodule-color-primary
        // token (which inherits through the shadow boundary) renders it in the site's
        // brand primary colour.
        return rowItem.status == TenantInitializationState.Available
          ? html`<webmodule-icon-button
              library="fa"
              name="fas-key"
              label=${tlang`Login to %%branch%%`}
              style="color: var(--webmodule-color-primary)"
              @click=${clickEvent}
            ></webmodule-icon-button>`
          : html``;
      }
    });

    return html` <div class=" branches-editor">
      <div class="mb-3">
        <div class="table-filter-header">
          <div class="filter-left">
            <button type="button" class="multi-action-btn btn-circle loginToDealer">
              <img class="icon action-key" src=${getAssetCdnPath('/assets/icons/key16.svg')} alt="" />
            </button>
            ${tlang`License Bundle:`}
            <span class="fw-bolder fs-1">${this.formatLicenseCount(this.data?.licenseData?.totalLicenses)}</span>
            ${tlang`Licenses Allocated:`}
            ${this.licenseSummaryValue(
              this.data?.licenseData?.licensesAllocatedToDealer,
              this.data?.licenseData?.totalLicenses
            )}
            ${tlang`Licenses Used:`}
            ${this.licenseSummaryValue(
              this.data?.licenseData?.licensesUsedByDealer,
              this.data?.licenseData?.totalLicenses
            )}
          </div>
          <div class="filter-right">${this.filterTemplate()}</div>
        </div>
      </div>

      <webmodule-table
        id="branches-table"
        fill-height
        .keyevent=${keyEvent}
        .columns=${columns}
        .elevated=${false}
        .cellBorders=${false}
        .uppercaseHeader=${false}
        .getGroupByKey=${this.groupByDealership}
        .groupHeaderTemplate=${this.renderGroupHeader}
        pageLength="100"
        @fetch=${(e: CustomEvent) => this.internalDataLoad(e)}
        .clickrows=${false}
      >
      </webmodule-table>
    </div>`;
  }

  public filterTemplate(): TemplateResult {
    const resetEvent = this.timedTrigger?.getResetEvent();
    const triggerEvent = this.timedTrigger?.getTriggerEarlyEvent();

    return html`
      <label for="filterText">${tlang`Filter:`}</label>
      <input
        .value=${this._filter ?? ''}
        type="text"
        id="filterText"
        placeholder="Text Search"
        @oninput=${resetEvent}
        @blur=${triggerEvent}
        @keyup=${resetEvent}
      />
    `;
  }

  async loginToFranchisee(item: FranchiseeBranchDeploymentView) {
    // Unavailable (emptyGuid) reports not ready yet.
    if (item.branchId === emptyGuid) {
      await information(tlang`Not available yet`);
      return;
    }
    this.dispatchCustom('login', {
      item: item
    });
  }

  editBranch(item: FranchiseeBranchDeploymentView) {
    if (item.branchId === emptyGuid) return;
    this.dispatchCustom('open-branch', {
      item: item
    });
  }

  editFranchisee(item: FranchiseeBranchDeploymentView) {
    this.dispatchCustom('open', { item: item });
  }

  dispatchCustom(name: string, values: object) {
    const options = {
      detail: { ...values },
      bubbles: true,
      composed: true
    };
    //wm-be webmodule-brancheditor
    this.dispatchEvent(new CustomEvent(`wm-tenant-${name}`, options));
  }

  connectedCallback(): void {
    super.connectedCallback();

    const triggerEvent = e => {
      this._filter = (e.target as HTMLInputElement).value;

      // Back to the first page, never a plain re-fetch. The page offset is meaningless
      // against a new filter: internalDataLoad slices the filtered dealerships at the old
      // offset, which lands past the end and returns an empty page - and once the filtered
      // set fits one page the paginator hides itself, leaving no way back. The setter
      // re-fetches even when the index is already 0.
      if (this.table) this.table.pageIndex = 0;
    };
    this.timedTrigger = new TimedTrigger(1500, triggerEvent);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    this.timedTrigger = undefined;
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  /** Dealership rows (with their branch children) that match the current filter. */
  private filteredDealerships(): FranchiseeNetworkRow[] {
    const dealerships = this.data?.branchDeployments ?? [];
    const filter = this._filter;
    if (!filter) return dealerships;

    const rowMatches = (r: FranchiseeBranchDeploymentView) =>
      containsText(r.sortName, filter) || containsText(r.sortBranchName, filter) || containsText(r.reference, filter);

    return dealerships
      .map(d => {
        // A dealership match keeps all its branches; otherwise keep only the
        // branches that match.
        const children = rowMatches(d) ? (d.children ?? []) : (d.children ?? []).filter(rowMatches);
        return { ...d, children };
      })
      .filter(d => rowMatches(d) || d.children.length > 0);
  }

  private zeroAsDashTemplate(value: number) {
    return value === 0 ? html`${tlang`${'ref:zerodash'}-`}` : html`${value}`;
  }

  /**
   * Formats an aggregate license count for the summary header. Returns a localized N/A when the value
   * cannot be calculated (missing/undefined source data aggregates to NaN, which `??` does not catch).
   */
  private formatLicenseCount(value: number | undefined): string | number {
    return Number.isFinite(value) ? (value as number) : tlang`N/A`;
  }

  /**
   * Renders an Allocated/Used summary value, highlighting the two problem states the user needs to see
   * instead of a raw number:
   *  - the value cannot be calculated (missing allocation data aggregates to NaN), or
   *  - more licenses are allocated/used than the license bundle provides (over-subscription).
   */
  private licenseSummaryValue(value: number | undefined, bundle: number | undefined): TemplateResult {
    if (!Number.isFinite(value)) {
      return html`<span
        class="fw-bolder fs-1 text-danger"
        title=${tlang`Allocation data is missing for one or more %%franchisee%%s and cannot be calculated.`}
      >
        ${tlang`Missing allocation`}
      </span>`;
    }
    if (Number.isFinite(bundle) && (value as number) > (bundle as number)) {
      return html`<span
        class="fw-bolder fs-1 text-danger"
        title=${tlang`This exceeds the license bundle of ${bundle}.`}
      >
        ${value}
      </span>`;
    }
    return html`<span class="fw-bolder fs-1">${value}</span>`;
  }

  /** This is called by the table when it wants new page data */
  private async internalDataLoad(e: CustomEvent) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const table = e.detail.table as WebModuleTable;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const index = (e.detail?.pageIndex as number) ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const length = (e.detail?.pageLength as number) ?? 1000;
    if (!this.data) {
      table.data = [];
      table.rowCount = 0;
      return;
    }

    // Paginate by dealership, then hand the table the flat branch rows for that page.
    // The table regroups them by dealership (see getGroupByKey) into a header + footer.
    const dealerships = this.filteredDealerships();
    const pageDealerships = dealerships.slice(index * length, index * length + length);
    table.data = pageDealerships.flatMap(d => d.children ?? []);
    table.rowCount = dealerships.length;
  }
}

export function getTenantStatusDisplayValue(status: TenantInitializationState, inUse: boolean): string {
  switch (status) {
    case TenantInitializationState.Available:
      return inUse
        ? tlang`${'ref:TenantInitializationState.AvailableInUse'}Active`
        : tlang`${'ref:TenantInitializationState.Available'}Ready`;
    case TenantInitializationState.Disabled:
      return tlang`${'ref:TenantInitializationState.Disabled'}Disabled`;
    case TenantInitializationState.PendingDelivery:
      return tlang`${'ref:TenantInitializationState.PendingDelivery'}Pending Delivery`;
    case TenantInitializationState.RequestedBySupplier:
      return tlang`${'ref:TenantInitializationState.RequestedBySupplier'}Requested By Supplier`;
  }
  return TenantInitializationState[status];
}

/** Maps a tenant status to a `<webmodule-tag>` variant for the group-header status badge. */
export function getTenantStatusVariant(
  status: TenantInitializationState,
  inUse: boolean
): 'primary' | 'success' | 'neutral' | 'warning' | 'danger' {
  switch (status) {
    case TenantInitializationState.Available:
      return inUse ? 'success' : 'primary';
    case TenantInitializationState.Disabled:
      return 'danger';
    case TenantInitializationState.PendingDelivery:
      return 'warning';
    case TenantInitializationState.RequestedBySupplier:
      return 'neutral';
  }
  return 'neutral';
}
